import { namespace, extend } from 'wsm-tagging-utils';
import userShouldBeTracked from '../../../../userShouldBeTracked';
import scripts, { scriptsConstants } from '../../../../scripts';

const runAdobeLaunchEventHandler = (win) => {
	namespace(win, 'DDC.tracking.adobe_launch.handlers');

	const {
		DDC,
		DDC: { tracking },
		innerHeight,
		innerWidth,
		newrelic
	} = win;

	DDC.tracking.adobe_launch.handlers.AdobeLaunchEventHandler =
		(function buildAdobeLaunchEventHandler() {
			// ======================================================================
			// ------------------ Private Variables and Functions -------------------
			// ======================================================================

			/**
			 * Verifies whether required third-party assets are available.
			 *
			 * @returns {boolean} Current status of Adobe libraries
			 */
			function isDTMReady(thisWin) {
				return typeof (thisWin || window)._satellite === 'object';
			}
			const FORM_EVENTS = [
				'ddc.form.onPage',
				'ddc.form.initiation',
				'ddc.form.submission',
				'ddc.form.confirmation'
			];

			// ======================================================================
			// ---------------------------- Constructor -----------------------------
			// ======================================================================

			function AdobeLaunchEventHandler(window, page, form) {
				tracking.framework.runtime.EventHandler.call(
					this,
					window,
					page,
					form
				);

				/**
				 * Utility class that performs data transformations and lookups.
				 * Instantiating in constructor rather than initialize() as the
				 * data mapper is called before initialize()
				 */
				this.dataMapper = new tracking.adobe_launch.util.DataMapper(
					this.win
				);

				this.defaultTemplateStrings = {};
			}

			extend(
				AdobeLaunchEventHandler,
				tracking.framework.runtime.EventHandler
			);

			// ======================================================================
			// ------------------------- Prototype Methods --------------------------
			// ======================================================================

			(function applyPrototypes(protoIn) {
				const proto = protoIn;

				proto.logCategory = 'AdobeLaunchEventHandler';

				proto.dealerBac = function dealerBac() {
					const dealerCodes = win.DDC.dataLayer.dealership.dealerCode;
					const foundGm = dealerCodes.find(
						(codeObject) =>
							typeof codeObject === 'object' && !!codeObject.gm
					);
					const foundNissan = dealerCodes.find(
						(codeObject) =>
							typeof codeObject === 'object' &&
							!!codeObject.nissanoem
					);
					const foundGmCabac = dealerCodes.find(
						(codeObject) =>
							typeof codeObject === 'object' &&
							!!codeObject.gmcabac
					);
					if (foundGm) {
						return foundGm.gm;
					} else if (foundNissan) {
						return foundNissan.nissanoem;
					} else if (foundGmCabac) {
						return foundGmCabac.gmcabac;
					} else {
						return 'Account {} not configured with a BAC dealer code';
					}
				};

				// ======================================================================
				// ------------------------ Lifecycle Operations ------------------------
				// ======================================================================

				/**
				 * This method is guaranteed to be called only after the configurable
				 * properties have been specified.
				 */
				proto.initialize = async function initialize() {
					this.log.info('initializing...');

					// build template string mapping from datalayer
					this.templateStrings = this.buildTemplateStrings();

					/**
					 * initalize digitalData object on window for page load event;
					 * page load event will send on _satellite.pageBottom()
					 */
					this.initData();

					// load the adobe launch script
					await scripts[scriptsConstants.DYNAMIC_SCRIPT_INSERT]({
						src: this.scriptLocation
					});

					this.log.info(
						`adobe launch script loaded: ${this.scriptLocation}`
					);

					window.dispatchEvent(
						new Event(this.waitOnEventToStartHandling)
					);

					this.log.info('initialized');
				};

				// ======================================================================
				// ----------------------------- Utilities ------------------------------
				// ======================================================================

				/**
				 * Returns the formName to send as FormInfo in digitalData
				 * @param {string} trackingId The DDC form tracking identifier
				 * @returns {string} The formName
				 */
				proto.mapFormTrackingId = function mapFormTrackingId(
					trackingId,
					element = ''
				) {
					const formTrackingIdMapping =
						this.dataMapper.getFormTrackingIdMapping();
					const formNameInfo =
						formTrackingIdMapping[
							typeof trackingId === 'string' &&
								trackingId.replace(/-/g, '_').toUpperCase()
						] || formTrackingIdMapping.DEFAULT;
					return {
						formType: formNameInfo.formType || '',
						formCategory: formNameInfo.formCategory || '',
						formName:
							this.determineFormName(
								formNameInfo.formName,
								element
							) || ''
					};
				};
				/**
				 * Returns whether the event came from the DDC DataLayer Event System (v9/widgets/tracking/ddc-data-layer/)
				 * MyCars has been instrumented with WidgetUserEvent so we opt to use those more accurate events now.
				 * @param {object} eventData data associated with the event
				 * @returns {boolean}
				 */
				proto.isMyCarsDataLayerEventSystemEvent =
					function isMyCarsDataLayerEventSystemEvent(eventData) {
						return (
							FORM_EVENTS.includes(eventData.eventName) &&
							(eventData.trackingId === 'MYCARS_REGISTER' ||
								eventData.eventCategory === 'mycars-register')
						);
					};

				// ======================================================================
				// ---------------------- Create Base Data Payload ----------------------
				// ======================================================================

				/**
				 * Builds the base data object with global properties set for page load.
				 * This initializes the global digitalData object to be populated on future events.
				 */
				proto.initData = function initData() {
					const data = {
						pageInfo: {
							pageName:
								'{pageBrands}:{pageRegion}:{country}:{language}:t3:{siteSectionsLevel5}',
							siteProvider: '{siteProvider}',
							siteEnvironment: '{siteEnvironment}',
							siteSectionsLevel1: '{siteSectionsLevel1}',
							siteSectionsLevel2: '{siteSectionsLevel2}',
							siteSectionsLevel3: '{siteSectionsLevel3}',
							siteSectionsLevel4: '{siteSectionsLevel4}',
							siteSectionsLevel5: '{siteSectionsLevel5}',
							siteTheme: this.page.dataLayerProperty(
								'site.design.themekit'
							),
							url: this.page.dataLayerProperty(
								'page.pageInfo.pageUrl'
							),
							seoStrategyPageName: '{pageTitle}',
							pageTool: '{pageTool}',
							pageType: '{pageType}',
							pageSubType: '{pageSubType}',

							languageSelected: '{language}',
							siteName: 'tier3',
							brand: '{brands}',
							country: '{country}',
							region: '{region}',

							vendorName: 'DealerDotCom',
							locationBac: this.dealerBac(),
							dealerAddress: `${this.page.dataLayerProperty(
								'dealership.address1'
							)}, ${this.page.dataLayerProperty(
								'dealership.city'
							)} ${this.page.dataLayerProperty(
								'dealership.stateProvince'
							)}`,
							dealerId: this.page.dataLayerProperty(
								'site.siteInfo.dealerId'
							),
							dealerName: this.page.dataLayerProperty(
								'dealership.dealershipName'
							),
							dealerZip: this.page.dataLayerProperty(
								'dealership.postalCode'
							),

							renderedExperience: '{deviceSize}',
							viewport: '{viewport}',
							orientation: '{orientation}'
						},
						visitorProfile: {
							zipCode: this.page.dataLayerProperty(
								'user.userPostalCode'
							)
						},
						vehicleInfo: {
							vehicleType: '{vehicle.bodyStyle}',
							modelYear: '{vehicle.modelYear}',
							make: '{vehicle.make}',
							modelName: '{vehicle.model}',
							bodyStyle: '{vehicle.bodyStyle}',
							trimLevel: '{vehicle.trim}',
							condition: '{vehicle.condition}',
							vin: '{vehicle.vin}',
							price: '{vehicle.internetPrice}'
						},
						internalCampaignInfo: {
							internalSearchTerms:
								this.dataMapper.getParamStringValue(
									'search',
									this.page.dataLayerProperty(
										'page.pageInfo.params'
									)
								)
						}
					};

					if (this.win.digitalData) {
						this.win.digitalData = {
							...this.win.digitalData,
							...this.parseData(data)
						};
					} else {
						this.win.digitalData = this.parseData(data);
					}

					this.log.info('base digitalData object initialized');
				};

				/**
				 * Returns this from the query facets:
				 * model-[model selected]|model-[other model selected]|color-[color selected]
				 *
				 * @param queryFacets
				 * @returns {string}
				 */
				proto.buildFilteredSearchFilters =
					function buildFilteredSearchFilters(queryFacets) {
						const filters = [];
						Object.keys(queryFacets).forEach((fType) => {
							// Exclude 'compositeType' and 'search' facets:
							// 	compositeType is used as a field in the events
							//	search is used for the typedSearch event and should not be included for filteredSearch
							if (
								fType !== 'compositeType' &&
								fType !== 'search'
							) {
								let fSelected;
								if (typeof queryFacets[fType] === 'string') {
									fSelected = queryFacets[fType].split(',');
									fSelected.forEach((fValue) => {
										filters.push(`${fType}-${fValue}`);
									});
								} else {
									filters.push(
										`${fType}-${queryFacets[fType]}`
									);
								}
							}
						});

						return filters.join('|');
					};

				/**
				 * Builds the site sections levels required by Adobe Launch specification
				 */
				proto.buildSiteSectionsLevels =
					function buildSiteSectionsLevels(alias, templateStrings) {
						let siteSectionsLevel1;
						let siteSectionsLevel2;
						let siteSectionsLevel3;
						let siteSectionsLevel4;
						let siteSectionsLevel5;

						const modelYear = templateStrings['vehicle.modelYear'];
						const model = templateStrings['vehicle.model'];

						// Format the page alias to be used in the pageName:
						// Remove the word "sitebuilder", remove trailing numbers, change underscores to spaces, and make lowercase.
						const formattedAlias = alias
							.replace(/(?:sitebuilder|(?:_\d+)+$)/gi, '')
							.replace(/_/g, ' ')
							.toLowerCase()
							.trim();

						/* only site sections levels 1-3 method of evaluation differ per page alias, 
                            4 & 5 are calculated using the same methods for each page alias */
						if (this.page.isVehicleDetailPage(alias)) {
							siteSectionsLevel1 = 'vehicles';
							siteSectionsLevel2 = `${modelYear} ${model}`;
						} else if (this.page.isVehicleListingPage(alias)) {
							siteSectionsLevel1 = 'vehicles';
						} else if (alias.match('.*MYCARS_.*FAVORITES.*')) {
							siteSectionsLevel1 = 'vehicles';
						} else if (this.page.isFinancePage(alias)) {
							siteSectionsLevel1 = 'financing';
						} else if (
							this.page.isOffersPage(alias) ||
							this.page.isIncentivesPage(alias) ||
							alias.indexOf('PROMOTIONS') > -1
						) {
							siteSectionsLevel1 = 'offers';
							if (
								alias.indexOf('INCENTIVES') > -1 &&
								this.page.dataLayerProperty(
									'page.attributes.incentives.0'
								)
							) {
								const {
									applicableCondition,
									currentVehicleModel,
									applicableYear
								} = this.page.dataLayerProperty(
									'page.attributes.incentives.0'
								);
								siteSectionsLevel2 = applicableCondition;
								siteSectionsLevel3 =
									currentVehicleModel &&
									applicableYear &&
									`${applicableYear} ${currentVehicleModel}`;
							}
						} else if (this.page.isToolPage(alias)) {
							siteSectionsLevel1 = 'tool';
						} else if (this.page.isServicePage(alias)) {
							siteSectionsLevel1 = 'services';
						} else if (
							this.page.isHoursOrDirectionsPage(alias) ||
							this.page.isAboutOrStaffPage(alias) ||
							this.page.isContactPage(alias)
						) {
							siteSectionsLevel1 = 'dealer-information';
						} else if (this.page.isCorporatePage(alias)) {
							siteSectionsLevel1 = 'corporate';
						} else if (alias.indexOf('SHOWROOM') > -1) {
							siteSectionsLevel1 = 'vehicles';
							siteSectionsLevel2 =
								modelYear && `${modelYear} ${model}`;
							if (siteSectionsLevel2) {
								siteSectionsLevel3 = 'Model Showroom';
							} else {
								siteSectionsLevel2 = 'Showroom';
							}
						}

						// Adding in the page alias to make the pageName more unique.
						// Pages differ in number of descriptors.
						// The page alias for Showroom pages is already included in siteSectionsLevel2 or siteSectionsLevel3.
						if (alias.indexOf('SHOWROOM') === -1) {
							if (
								siteSectionsLevel1 &&
								siteSectionsLevel2 &&
								siteSectionsLevel3
							) {
								siteSectionsLevel4 = formattedAlias;
							} else if (
								siteSectionsLevel1 &&
								siteSectionsLevel2
							) {
								siteSectionsLevel3 = formattedAlias;
							} else if (siteSectionsLevel1) {
								siteSectionsLevel2 = formattedAlias;
							} else {
								siteSectionsLevel1 = formattedAlias;
							}
						}

						siteSectionsLevel5 =
							(siteSectionsLevel1 && `${siteSectionsLevel1}`) ||
							'';
						siteSectionsLevel5 +=
							(siteSectionsLevel2 && `:${siteSectionsLevel2}`) ||
							'';
						siteSectionsLevel5 +=
							(siteSectionsLevel3 && `:${siteSectionsLevel3}`) ||
							'';
						siteSectionsLevel5 +=
							(siteSectionsLevel4 && `:${siteSectionsLevel4}`) ||
							'';

						return {
							siteSectionsLevel1,
							siteSectionsLevel2,
							siteSectionsLevel3,
							siteSectionsLevel4,
							siteSectionsLevel5
						};
					};

				/**
				 * Builds template string mapping from properties parsed from the window
				 * and datalayer. Utilizes the DataMapper utility to parse window.
				 *
				 * @returns {Object} A mapping of template strings to values
				 */
				proto.buildTemplateStrings = function buildTemplateStrings() {
					const pageAlias = this.page.currentPageAlias();
					const templateStrings = { ...this.defaultTemplateStrings };

					templateStrings.pageTitle = $('head title').text();
					templateStrings.siteProvider = 'DDC';
					templateStrings.siteEnvironment = this.dataMapper.lookup(
						'DDC.isLive'
					)
						? 'prod'
						: 'nonprod';
					if (DDC?.WidgetData?.adobe_launch) {
						templateStrings.pageType =
							this.dataMapper.getPageAliasMapping().pageType ||
							'custom';
						templateStrings.pageSubType =
							this.dataMapper.getPageAliasMapping().pageSubType;
						templateStrings.pageTool =
							this.dataMapper.getPageAliasMapping().pageTool;
					}

					let clientDevice;
					if (this.dataMapper.lookup('DDC.client.isTablet')) {
						clientDevice = 'tablet';
					} else if (this.dataMapper.lookup('DDC.client.isMobile')) {
						clientDevice = 'mobile';
					} else {
						clientDevice = 'desktop';
					}
					templateStrings.deviceSize = this.dataMapper.map(
						`clientDevice.${clientDevice}`
					);

					// 2-character ISO 639 - Alpha-2 code
					templateStrings.language = this.page
						.dataLayerProperty('page.attributes.locale')
						.substr(0, 2)
						.toLowerCase();

					// 2-character value for the pageName, full string for the 'brand' property
					const siteFranchises =
						this.page.dataLayerProperty('site.franchises');
					templateStrings.pageBrands = this.dataMapper.mapFranchises(
						siteFranchises,
						true
					);
					templateStrings.brands =
						this.dataMapper.mapFranchises(siteFranchises);

					// ISO 3166 - Alpha-2 code
					templateStrings.country = this.page
						.dataLayerProperty('dealership.country')
						.toLowerCase();

					// <width>x<height> in pixels
					templateStrings.viewport = `${innerWidth}x${innerHeight}`;

					// window orientation for mobile
					let orientation =
						this.win.screen.msOrientation ||
						this.win.screen.mozOrientation ||
						(this.win.screen.orientation || {}).type;
					if (!orientation) {
						orientation =
							this.win.screen.width > this.win.screen.height
								? 'landscape-primary'
								: 'portrait-primary';
					}
					templateStrings.orientation = this.dataMapper.map(
						`orientation.${orientation}`
					);

					// Determine vehicle information
					if (
						this.page.dataLayerProperty(
							'page.pageInfo.isShowroomOverview'
						)
					) {
						// parse vehicle information from URL if on showroom
						let path = this.page.dataLayerProperty(
							'page.pageInfo.pagePath'
						);

						if (typeof path === 'string' && path !== '') {
							path = path.split('/');

							// filter out unwanted components of path
							path = path.filter((a) => {
								return a !== '' && a !== 'showroom';
							});

							// use remaining path components to supply vehicle information
							if (path.length === 4) {
								[
									templateStrings['vehicle.modelYear'],
									templateStrings['vehicle.make']
								] = path;
								templateStrings['vehicle.model'] =
									path[2].replace('+', ' ');
								templateStrings['vehicle.bodyStyle'] =
									path[3].replace(/\..*/, '');
							}
						}
					} else if (
						(this.page.isVehicleDetailPage(pageAlias) &&
							this.page.dataLayerProperty('vehicles') &&
							this.page.dataLayerProperty('vehicles').length ===
								1) ||
						this.page.dataLayerProperty('confirmation.vehicle')
					) {
						const formConfirmationData =
							this.page.dataLayerProperty('confirmation');
						let { inventoryType } =
							this.page.dataLayerProperty('vehicles.0') ||
							this.page.dataLayerProperty('confirmation.vehicle');
						const {
							modelYear,
							make,
							model,
							bodyStyle,
							trim,
							vin,
							internetPrice
						} =
							this.page.dataLayerProperty('vehicles.0') ||
							this.page.dataLayerProperty('confirmation.vehicle');

						if (!inventoryType) {
							if (
								typeof formConfirmationData === 'object' &&
								typeof formConfirmationData.type === 'string'
							) {
								inventoryType = formConfirmationData.type;
							} else {
								inventoryType = 'default';
							}
						}

						templateStrings['vehicle.modelYear'] = modelYear;
						templateStrings['vehicle.make'] = make;
						templateStrings['vehicle.model'] = model;
						templateStrings['vehicle.bodyStyle'] = bodyStyle;
						templateStrings['vehicle.trim'] = trim;
						templateStrings['vehicle.vin'] = vin;
						templateStrings['vehicle.condition'] =
							this.dataMapper.map(
								`inventoryType.${inventoryType}`
							);
						templateStrings['vehicle.internetPrice'] =
							internetPrice;
					}

					// Build site sections and merge with template strings
					return {
						...templateStrings,
						...this.buildSiteSectionsLevels(
							pageAlias,
							templateStrings
						)
					};
				};

				/**
				 * Parses string for template strings and replaces with mapped value.
				 * If no mapped value is found in template strings mapping will attempt
				 * to grab valid value from datalayer and window.
				 *
				 * @param {string} str String to perform search and replace
				 * @returns {string} The input string with template strings replaced
				 */
				proto.parseTemplateStrings = function parseTemplateStrings(
					strIn
				) {
					let str = strIn;

					if (typeof str === 'string') {
						// replace templates in string denoted by {key}
						str = str.replace(/{(.*?)}/g, (match, key) => {
							return (
								this.templateStrings[key] ||
								this.page.dataLayerProperty(key) ||
								this.dataMapper.lookup(key) ||
								''
							);
						});

						// set to the string 'undefined' if string only contained templates
						// and no matches found
						if (str.replace(/:/g, '') === '') {
							str = 'undefined';
						}
					}

					return str;
				};

				/**
				 * Formats data payload values. Parses template strings on payloads's
				 * string properties.
				 *
				 * @param {Object} baseData Event payload to parse for template strings
				 * @returns {Object} A copy of the event payload with template strings replaced
				 */
				proto.parseData = function parseData(baseData) {
					// copy original data object so modifications only affect copy
					const data = this.dataMapper.mergeObjects({}, baseData);

					// parse template strings in data payload
					if (typeof data === 'object' && data !== null) {
						Object.keys(data).forEach((prop) => {
							// parse nested objects
							if (typeof data[prop] === 'object') {
								data[prop] = this.parseData(data[prop]);
							} else if (typeof data[prop] === 'string') {
								data[prop] = this.parseTemplateStrings(
									data[prop]
								);
								const value = data[prop];

								// clean up undefined keys
								if (value !== 'undefined') {
									data[prop] = this.parseTemplateStrings(
										data[prop]
									);
								} else {
									delete data[prop];
								}
							}
						});
					}

					return data;
				};

				// ======================================================================
				// ---------------------------- Send Events -----------------------------
				// ======================================================================

				/**
				 * Initiates sending of event data payload to Adobe Launch. Used only
				 * for events that occur post-page load.
				 *
				 * @param {string} directCallEvent The event tracking type.
				 *  Defaults to 'global-onclick'.
				 * @param {object} additionalData JSON object containing additional properties relevant to the event which are not stored in digitalData
				 */
				proto.sendData = async function sendData(
					directCallEvent,
					additionalData
				) {
					let waitForSatellite;

					if (!this.satelliteLoaded) {
						waitForSatellite = new Promise((resolve) => {
							const interval = setInterval(() => {
								if (isDTMReady(this.win)) {
									clearInterval(interval);
									this.satelliteLoaded = true;
									resolve();
								}
							}, 10);
						});
					} else {
						waitForSatellite = Promise.resolve();
					}

					await waitForSatellite;

					// exit if required Adobe assets are unavailable
					if (!isDTMReady(this.win)) {
						this.log.error(
							'_satellite object not defined - cannot send event'
						);
						return;
					}

					if (typeof directCallEvent !== 'string') {
						this.log.error(
							'invalid direct call event value, unable to send data'
						);
						return;
					}

					// parse any additional template strings added to the data payload
					const digitaldata = this.parseData(this.win.digitalData);

					// trigger sending of event with the appropriate track type
					this.log.info(
						`digitalData: \n ${JSON.stringify(
							this.win.digitalData,
							null,
							2
						)}`
					);
					this.log.info(
						`sending "${directCallEvent}" event to Adobe Launch`
					);

					let trackArgs;
					if (additionalData && typeof additionalData === 'object') {
						this.log.info(
							`additionalData: \n${JSON.stringify(
								additionalData,
								null,
								2
							)}`
						);
						trackArgs = [directCallEvent, additionalData];
					} else {
						trackArgs = [directCallEvent];
					}

					if (this.win?._satellite?.track) {
						// track?.() is EXTRA safe just in case for whatever reason 'track' is an object or string or something
						this.win._satellite.track?.(...trackArgs);
					} else {
						this.log.error(
							'Missing window._satellite.track function, can not send data to Adobe Launch!'
						);
					}

					if (win.newrelic) {
						if (userShouldBeTracked()) {
							const pageActionAttributes = {
								eventHandler: 'AdobeLaunchEventHandler',
								eventName: directCallEvent,
								useFormAPI: true, // always true via useFormAPI: true
								mappingSource: 'legacy ws-tagging'
							};
							newrelic.addPageAction(
								`${this.project}_send_data`,
								pageActionAttributes
							);
						}
					}

					// Send event to BCF event viewer.
					// Figure out how to have event be the DDC event (ex: ddc.link.generic).
					window.dispatchEvent(
						new CustomEvent('eventViewerData', {
							detail: {
								common: {
									project: this.project,
									event: directCallEvent,
									source: 'ws-tagging',
									payload: {
										...digitaldata,
										...additionalData
									},
									eventHandler: this.eventHandlerName
								}
							}
						})
					);
				};

				// ======================================================================
				// --------------------------- Event Handlers ---------------------------
				// ======================================================================

				// Page View Events
				proto.handlePageView = function handlePageView(_eventData) {
					this.sendData('general.bottom.of.page');
				};

				proto.handleVehicleDetailView =
					function handleVehicleDetailView(_eventData) {
						this.sendData('vdp.bottom.of.page');
					};

				// Widget User Events
				proto.handleWidgetUserEvent = function handleWidgetUserEvent(
					eventData
				) {
					const widgetUserEventConstants =
						win.DDC.Widgets?.MyCars.WIDGET_USER_EVENT_CONSTANTS
							.FORM;
					switch (eventData.widgetName) {
						case 'ws-inv-text-search':
							this.win.digitalData.formInfo = {
								formFieldName: eventData.activeElementName,
								formName: eventData.formName
							};
							this.sendData('form.initiation', {
								customLink: `${eventData.section}:${eventData.formName} - ${eventData.activeElementName}`,
								pageName: this.dataMapper.lookup(
									'digitalData.pageInfo.pageName'
								),
								position: eventData.section,
								formName: eventData.formName
							});
							break;
						case 'ws-site-text-search':
							this.win.digitalData.formInfo = {
								formFieldName: eventData.activeElementName,
								formName: eventData.formName
							};
							this.sendData('form.initiation', {
								customLink: `${eventData.section}:${eventData.formName} - ${eventData.activeElementName}`,
								pageName: this.dataMapper.lookup(
									'digitalData.pageInfo.pageName'
								),
								position: eventData.section,
								formName: eventData.formName
							});
							break;
						case 'ws-vehicle-media':
							// Handle all vehicle-media events except MyCars Save
							// because that event gets handled in this.handleMycarsSave
							if (
								eventData.result !== 'Favorite added' &&
								eventData.result !== 'Vehicle saved'
							) {
								this.handleVehicleMediaEvent(eventData);
							}
							break;
						case 'mycars-default':
							// MyCars Form Shown
							if (
								[
									widgetUserEventConstants.LOGIN.IMPRESS
										.result,
									widgetUserEventConstants.REGISTER.IMPRESS
										.result,
									widgetUserEventConstants.FORGOT.IMPRESS
										.result
								].includes(eventData.result)
							) {
								this.handleFormOnPage(eventData);
							}

							// MyCars Form Interaction
							if (
								eventData.result ===
									widgetUserEventConstants.FORGOT.INTERACT
										.result ||
								eventData.result ===
									widgetUserEventConstants.LOGIN.INTERACT
										.result ||
								eventData.result ===
									widgetUserEventConstants.REGISTER.INTERACT
										.result
							) {
								this.handleFormInitiation(eventData);
							}

							// MyCars Form Submission Pass/Fail
							if (
								eventData.result ===
									widgetUserEventConstants.FORGOT.SUBMIT
										.result ||
								eventData.result ===
									widgetUserEventConstants.LOGIN.SUBMIT
										.result ||
								eventData.result ===
									widgetUserEventConstants.REGISTER.SUBMIT
										.result ||
								eventData.result ===
									widgetUserEventConstants.FORGOT.FAIL
										.result ||
								eventData.result ===
									widgetUserEventConstants.LOGIN.FAIL
										.result ||
								eventData.result ===
									widgetUserEventConstants.REGISTER.FAIL
										.result ||
								eventData.result ===
									widgetUserEventConstants.REGISTER.EXISTING
										.result
							) {
								this.handleFormSubmission(eventData);
							}

							// MyCars Form Confirmation
							if (
								eventData.result ===
									widgetUserEventConstants.FORGOT
										.CONFIRM_IMPRESS.result ||
								eventData.result ===
									widgetUserEventConstants.LOGIN
										.CONFIRM_IMPRESS.result ||
								eventData.result ===
									widgetUserEventConstants.REGISTER
										.CONFIRM_IMPRESS.result
							) {
								this.handleFormConfirmation(eventData);
							}

							// MyCars Form Logout
							if (
								eventData.result ===
								widgetUserEventConstants.LOGOUT.CLICK.result
							) {
								this.sendData('global-onClick', eventData);
							}
							break;
						default:
							break;
					}
				};

				// Link Events
				proto.handleClickToCall = function handleClickToCall(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { clickToCallDepartment, phoneNumber, section } =
						eventData;

					this.sendData('telephone.dial', {
						customLink: `${section}:${clickToCallDepartment}:call`,
						position: section,
						pageName,
						department: clickToCallDepartment,
						phoneNumber
					});
				};

				proto.handleComparePageClick = function handleComparePageClick(
					eventData
				) {
					let linkText;
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const position = 'inventory';
					const {
						bodyStyle,
						element,
						make,
						model,
						trim,
						type,
						vin,
						year
					} = eventData;

					const { firstElementChild, innerText } = element;

					if (
						firstElementChild &&
						firstElementChild.classList.contains('photo')
					) {
						const imgAlt = firstElementChild.getAttribute('alt');

						if (imgAlt) {
							linkText = imgAlt;
						} else {
							linkText = `Image ${vin}`;
						}
					} else {
						linkText = innerText;
					}

					this.sendData('link.compare.page.click', {
						customLink: `${position}:${vin}`,
						linkText,
						pageName,
						position,
						vehicleInfo: {
							bodyStyle,
							make,
							modelName: model,
							modelYear: year,
							trimLevel: trim,
							vehicleType: type,
							vin
						}
					});
				};

				proto.handleGetDirections = function handleGetDirections(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { linkText, section } = eventData;

					this.sendData('link.get.directions', {
						customLink: `${section}:${linkText}`,
						position: section,
						pageName,
						linkText
					});
				};

				proto.handleSlideshowPagerClick =
					function handleSlideshowPagerClick(eventData) {
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const {
							destinationIndex,
							element,
							linkText,
							linkUrl,
							currentItem,
							totalItems,
							position
						} = eventData;

						this.sendData('text.link', {
							customLink: `${position}:pager:${destinationIndex}`,
							element: JSON.stringify(element, [
								'id',
								'className',
								'href',
								'tagName',
								'innerText',
								'type'
							]),
							interactionValue: `${currentItem}/${totalItems}`,
							interactionLabel: 'dots',
							linkUrl,
							linkText,
							pageName,
							position
						});
					};

				proto.handleLinkClick = function handleLinkClick(eventData) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const {
						element,
						text,
						linkType,
						section,
						url,
						currentItem,
						totalItems,
						vehicle
					} = eventData;
					const additionalData = {
						customLink: `${section}:${text || undefined}`,
						element:
							(element &&
								JSON.stringify(element, [
									'id',
									'className',
									'href',
									'tagName',
									'innerText',
									'type'
								])) ||
							'',
						pageName,
						position: section,
						linkText: text,
						linkUrl: url || ''
					};
					Object.assign(additionalData, vehicle);

					if (
						eventData.slidePositionPostClick &&
						eventData.slideTotal &&
						(linkType.includes('next') || linkType.includes('prev'))
					) {
						const { slidePositionPostClick, slideTotal } =
							eventData;

						// Get the previous slide position because slideshows set the next/prev slide to active when you click
						// the button and then this code is hit. Consumers of this data want to know the slide that was showing
						// when the arrows were clicked.
						let position;

						if (eventData.linkType.includes('next')) {
							position =
								slidePositionPostClick === 1
									? slideTotal
									: slidePositionPostClick - 1;
						} else {
							position =
								slidePositionPostClick === slideTotal
									? 1
									: slidePositionPostClick + 1;
						}

						additionalData.interactionValue = `${position}/${slideTotal}`;
					}

					if (eventData.slideControlLabel) {
						additionalData.interactionLabel =
							eventData.slideControlLabel.trim();
					}

					if (eventData.label) {
						additionalData.searchFilterLabel =
							eventData.label.trim();
					}

					if (eventData.expand) {
						additionalData.expand = eventData.expand;
					}

					if (eventData.showroomSectionHeading) {
						additionalData.interactionLabel =
							eventData.showroomSectionHeading.trim();
					}

					if (eventData.uniqueEvent) {
						additionalData.uniqueEvent = eventData.uniqueEvent;
					}

					// originally set up for /video-gallery/index.htm previous/next arrow tracking
					if (currentItem && totalItems) {
						additionalData.interactionValue = `${currentItem}/${totalItems}`;
					}

					if (eventData.interactionValue) {
						additionalData.interactionValue =
							eventData.interactionValue;
					}

					if (eventData.label) {
						additionalData.label = eventData.label;
					}

					// linkType dynamically predetermined via LinkTypeDecorator
					// section and text dynamically predetermined via CustomLinkDecorator
					// prefer `undefined` over empty string for CTA text
					this.sendData(linkType, additionalData);
				};

				proto.handleNavigationClick = function handleNavigationClick(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { linkText, position, section, url } = eventData;

					this.sendData('nav.link', {
						customLink: `${section}:${linkText}`,
						position,
						pageName,
						linkText,
						linkUrl: url
					});
				};

				proto.handleSocialHeaderClick =
					function handleSocialHeaderClick(eventData) {
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const position = 'Global Navigation';
						const { service, url } = eventData;

						this.sendData('link.social.header.click', {
							customLink: `${position}:${service}`,
							position,
							pageName,
							linkText: service,
							url
						});
					};

				proto.handleSocialShare = function handleSocialShare(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { section, service, url } = eventData;

					this.sendData('footer.social.icons', {
						customLink: `${section}:${service}`,
						position: section,
						pageName,
						linkText: service,
						url
					});
				};

				proto.handleRecommendedVehicleClick =
					function handleRecommendedVehicleClick(eventData) {
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const { section, url, text } = eventData;
						this.sendData('link.recommended.vehicles', {
							customLink: `${section}:${text}`,
							position: section,
							pageName,
							url
						});
					};

				// Inventory Events
				proto.handleFilterSearch = function handleFilterSearch(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const {
						position = 'inventory',
						queryFacets: { search },
						resultCount
					} = eventData;
					const queryFacets = this.buildFilteredSearchFilters(
						eventData.queryFacets
					);

					if (!search) {
						this.sendData('filtered.search.results', {
							customLink: `${position}:${queryFacets}`,
							position,
							pageName,
							resultCount,
							queryFacets
						});
					}
				};

				proto.handleFilterSearchClick =
					function handleFilterSearchClick(eventData) {
						const {
							checked,
							clickedFacetName,
							clickedFacetValue,
							facets,
							isSlider
						} = eventData;
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const position = 'inventory';
						let customLink = `${position}:${clickedFacetName}-${clickedFacetValue}`;

						if (!isSlider) {
							customLink += `:${
								checked ? 'checked' : 'unchecked'
							}`;
						}

						this.sendData('filtered.search.click', {
							customLink,
							position,
							pageName,
							queryFacets: facets,
							inventoryFilterLabel: clickedFacetName,
							inventoryFilterValue: clickedFacetValue
						});
					};

				proto.handleTypedSearchInit = function handleTypedSearchInit(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const position = 'inventory';
					const { search } = eventData;

					this.sendData('typed.search.init', {
						customLink: `${position}:${search || undefined}`,
						position,
						pageName,
						search
					});
				};

				proto.handleTypedSearchResults =
					function handleTypedSearchResults(eventData) {
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const {
							position = 'inventory',
							queryFacets: { search },
							resultCount
						} = eventData;

						this.sendData('typed.search.results', {
							customLink: `${position}:${search}`,
							position,
							pageName,
							resultCount,
							queryFacets: search
						});
					};

				proto.handleIncentiveFilter = function handleIncentiveFilter(
					eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const checked = eventData.checked ? 'checked' : 'unchecked';
					this.sendData('incentive.filter', {
						customLink: `incentives:${eventData.clickedFacetName}-${eventData.clickedFacetValue}:${checked}`,
						position: 'incentives',
						pageName,
						queryFacets: eventData.facets
					});
				};

				// Form Events
				proto.handleFormChange = function handleFormChange(eventData) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const {
						element,
						event,
						firstOptionText,
						label,
						name,
						section,
						selectedValue
					} = eventData;

					let eventName;
					const additionalData = {
						customLink: `${section}:${label}`,
						position: section,
						pageName,
						toggle: label || name
					};

					switch (event) {
						case 'ddc.form.inventorySearchLeadChange':
							eventName = 'form.inventory.search.lead.change';
							break;
						case 'ddc.form.facetBrowseChange':
							eventName = 'form.facet.browse.change';
							// Special handling for video gallery facetBrowseChange
							if (
								element &&
								$(element).parents('.video-gallery').length > 0
							) {
								// 'label' should be the text of the first option field
								if (firstOptionText) {
									additionalData.label = firstOptionText;
								}
								// 'value' should be the value attribute of the selected option
								if (selectedValue) {
									additionalData.value = selectedValue;
								} else if (label) {
									// 'value' should be the label as a fallback
									// handles setting value on the first option which doesn't have a value attribute
									additionalData.value = label;
								}
							}
							break;
						case 'ddc.form.slideChange':
							eventName = 'ddc.form.slideChange';
							break;
						default:
							break;
					}

					if (eventName) {
						this.sendData(eventName, additionalData);
					}
				};

				proto.handleFormInitiation = function handleFormInitiation(
					eventData
				) {
					this.form
						.getFormMapping(eventData.trackingId)
						.then((formMapping) => {
							if (
								!this.isMyCarsDataLayerEventSystemEvent(
									eventData
								)
							) {
								const pageName = this.dataMapper.lookup(
									'digitalData.pageInfo.pageName'
								);
								let { formName } = formMapping;
								const { formType, formCategory } = formMapping;
								formName = this.determineFormName(
									formName,
									eventData.element
								);
								this.win.digitalData.formInfo = {
									formName
								};
								const inputs = ['input', 'select', 'textarea'];
								const { activeElement } = document;

								if (
									inputs.indexOf(
										activeElement.tagName.toLowerCase()
									) !== -1
								) {
									this.win.digitalData.formInfo.formFieldName =
										activeElement.name;
								}

								this.sendData('form.initiation', {
									customLink: `${eventData.section}:${formName} - ${activeElement.name}`,
									pageName,
									position: eventData.section,
									formLeadId: eventData.formEventId || '',
									formName,
									formType,
									formCategory
								});
							}
						})
						.catch((error) => {
							this.log.error(error);
						});
				};

				proto.handleFormConfirmation = function handleFormConfirmation(
					eventData
				) {
					const trackingId = eventData.trackingId || 'DEFAULT';
					this.form
						.getFormMapping(trackingId)
						.then((formMapping) => {
							if (
								!this.isMyCarsDataLayerEventSystemEvent(
									eventData
								)
							) {
								const pageName = this.dataMapper.lookup(
									'digitalData.pageInfo.pageName'
								);
								const { formEventId, element } = eventData;
								let { formName } = formMapping;
								const { formType, formCategory } = formMapping;

								formName = this.determineFormName(
									formName,
									element
								);

								if (
									localStorage.getItem(
										`adobe_launch-formName.${formEventId}`
									)
								) {
									formName = localStorage.getItem(
										`adobe_launch-formName.${formEventId}`
									);
									localStorage.removeItem(
										`adobe_launch-formName.${formEventId}`
									);
								}

								this.win.digitalData.formInfo = {
									formName
								};

								const pageAlias =
									DDC.dataLayer.confirmation.pageName ||
									this.page.currentPageAlias();

								if (this.page.isVehicleDetailPage(pageAlias)) {
									formName = `VDP:${formName}`;
								} else if (
									this.page.isVehicleListingPage(pageAlias)
								) {
									formName = `VLP:${formName}`;
								}
								const data = {
									formName,
									pageName,
									formCategory: formCategory || 'lead',
									formType: formType || trackingId,
									formLeadId: eventData.formEventId || ''
								};
								if (eventData.initiatingClickText) {
									data.initiatingClickText =
										eventData.initiatingClickText;
								}
								this.sendData('form.confirmation', data);
							}
						})
						.catch((error) => {
							this.log.error(error);
						});
				};

				proto.determineFormName = function determineFormName(
					formNameMap,
					element
				) {
					const foundFormNameSelector = Object.keys(formNameMap).find(
						(formNameSelector) =>
							$(element).closest(formNameSelector).length > 0
					);

					if (foundFormNameSelector) {
						return formNameMap[foundFormNameSelector];
					}

					return formNameMap.default;
				};

				proto.handleFormSubmission = function handleFormSubmission(
					eventData
				) {
					this.form
						.getFormMapping(eventData.trackingId)
						.then((formMapping) => {
							if (
								!this.isMyCarsDataLayerEventSystemEvent(
									eventData
								)
							) {
								const pageName = this.dataMapper.lookup(
									'digitalData.pageInfo.pageName'
								);
								const {
									invalidFields = {},
									isModal,
									section,
									trackingId,
									formEventId,
									element
								} = eventData;
								const invalidFieldNames =
									Object.keys(invalidFields);

								let { formName } = formMapping;
								const { formType, formCategory } = formMapping;
								formName = this.determineFormName(
									formName,
									element
								);

								this.win.digitalData.formInfo = {
									formName
								};

								// compile errors on form and apply to digitalData
								if (invalidFieldNames.length > 0) {
									const errorMessages = invalidFieldNames.map(
										(fieldName) =>
											`${fieldName}: ${invalidFields[fieldName]}`
									);
									this.win.digitalData.formInfo.errorMessage =
										errorMessages.join();
								}

								localStorage.setItem(
									`adobe_launch-formName.${formEventId}`,
									formName
								);

								this.sendData('form.submission', {
									customLink: `${section}:${formName}`,
									formName,
									isModal,
									pageName,
									trackingId,
									position: section,
									formLeadId: eventData.formEventId || '',
									formType,
									formCategory
								});
							}
						})
						.catch((error) => {
							this.log.error(error);
						});
				};

				proto.handlePaymentCalcInit = function handlePaymentCalcInit(
					_eventData
				) {
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const position = 'finance';

					this.sendData('form.payment.calc.init', {
						customLink: `${position}:paymentCalculator`,
						position,
						pageName
					});
				};

				proto.handleFormOnPage = function handleFormOnPage(eventData) {
					const { trackingId, vehicle, element, formEventId } =
						eventData;
					this.form
						.getFormMapping(trackingId)
						.then((formMapping) => {
							if (
								!this.isMyCarsDataLayerEventSystemEvent(
									eventData
								)
							) {
								let { formName } = formMapping;
								const { formType, formCategory } = formMapping;
								formName = this.determineFormName(
									formName,
									element
								);
								this.win.digitalData.formInfo = {
									formName
								};

								const data = {
									formName,
									formLeadId: formEventId || '',
									formType,
									formCategory
								};

								if (vehicle && vehicle.bodyStyle) {
									this.sendData(
										'form.show',
										Object.assign(vehicle, data)
									);
								} else {
									this.sendData('form.show', data);
								}
							}
						})
						.catch((error) => {
							this.log.error(error);
						});
				};

				proto.handleTypedSearch = function handleTypedSearch(
					eventData
				) {
					const position = 'Typed Search Form';
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { typedSearchContent } = eventData;

					this.sendData('typed.search.submit', {
						customLink: `${position}:${typedSearchContent}`,
						position,
						pageName,
						typedSearchContent
					});
				};

				// Dialog Events
				proto.handleAjaxContentLoad = function handleAjaxContentLoad(
					eventData
				) {
					this.sendData('dialog.ajax.content.loaded', {
						hasForm: eventData.hasForm
					});
				};

				proto.handleDialog = function handleDialog(eventData, action) {
					const dialog = eventData.element.closest('.ui-dialog');
					const dialogTitleEl =
						dialog && dialog.querySelector('.ui-dialog-title');
					const dialogTitle =
						dialogTitleEl && dialogTitleEl.innerText.trim();
					const position = 'dialog';
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);

					this.sendData(`dialog.${action}`, {
						customLink: `${position}:${dialogTitle}`,
						position,
						pageName
					});
				};

				proto.handleDialogOpened = function handleDialogOpened(
					eventData
				) {
					this.handleDialog(eventData, 'opened');
				};

				proto.handleDialogClosed = function handleDialogClosed(
					eventData
				) {
					this.handleDialog(eventData, 'closed');
				};

				// Slideshow Events
				proto.handleSlideshowClick = function handleSlideshowClick(
					eventData
				) {
					const { element, name, position, section, url } = eventData;
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);

					this.sendData('image.link', {
						customLink: `${section}:${
							name || element.className || ''
						}`,
						element: JSON.stringify(element, [
							'id',
							'className',
							'href',
							'tagName',
							'innerText',
							'type'
						]),
						linkUrl: url,
						pageName,
						position: section,
						slideOrder: position
					});
				};

				// ws-vehicle-media Events
				proto.handleVehicleMediaEvent =
					function handleVehicleMediaEvent(eventData) {
						const {
							action,
							element,
							result,
							optionalData = {},
							widgetState
						} = eventData;
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);

						let current;
						let total;

						if (Object.keys(optionalData).length) {
							current = optionalData.currentItem;
							total = optionalData.totalItems;
						}

						let linkType = 'image.link';
						if (action === 'swiped') {
							linkType = 'carousel swiped';
						} else {
							const arrowButtonMatch =
								element.match(/(?:next|previous)/i);
							if (arrowButtonMatch) {
								linkType = `arrow.${arrowButtonMatch[0]
									.substring(0, 4)
									.toLowerCase()}`;
							} else if (/button/i.test(element)) {
								linkType = 'button.link';
							}
						}

						const additionalData = {
							customLink: `Carousel:${widgetState}:${element}`,
							element,
							pageName,
							position: 'Carousel',
							result
						};

						if (current && total) {
							additionalData.interactionValue = `${current}/${total}`;
						}

						if (element.includes('Thumbnail Carousel')) {
							additionalData.interactionLabel = 'thumbnail';
						} else if (element.includes('Next')) {
							additionalData.interactionLabel = 'next';
						} else if (element.includes('Previous')) {
							additionalData.interactionLabel = 'previous';
						}

						this.sendData(linkType, additionalData);
					};

				// Offer Click Events
				proto.handleOfferClick = function handleOfferClick(eventData) {
					const { offerId, offerName, section } = eventData;

					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const { incentives = [] } =
						this.page.dataLayerProperty('page.attributes');
					const applicableIncentive =
						Array.isArray(incentives) &&
						incentives.find(({ id }) => id === offerId);

					const offerClick = {
						customLink: `${section}:${offerId}`,
						position: section,
						pageName,
						offerId,
						offerName
					};

					if (
						applicableIncentive &&
						Object.keys(applicableIncentive).length > 0
					) {
						const {
							currentVehicleModel,
							currentVehicleBodyStyle,
							applicableTrims,
							applicableYear,
							applicableMake,
							type
						} = applicableIncentive;

						let currentVehicleModelRevised = currentVehicleModel;
						if (
							applicableMake &&
							currentVehicleModel &&
							currentVehicleModel.match(applicableMake)
						) {
							currentVehicleModelRevised =
								currentVehicleModel.replace(
									`${applicableMake} `,
									''
								);
						}

						let currentVehicleTrims;
						if (
							Array.isArray(applicableTrims) &&
							applicableTrims.length > 0
						) {
							const trimMatcher = `${applicableYear} ${applicableMake} ${currentVehicleModel} ${currentVehicleBodyStyle} `;

							currentVehicleTrims = applicableTrims.reduce(
								(trimsIn, trim) => {
									let trims = trimsIn;
									trims +=
										trim.indexOf(trimMatcher) > -1 &&
										`${trims && '|'}${trim.replace(
											trimMatcher,
											''
										)}`;

									return trims;
								},
								''
							);
						}

						Object.assign(offerClick, {
							offerType: type,
							vehicleBodyStyle: currentVehicleBodyStyle,
							vehicleModel: currentVehicleModelRevised,
							vehicleTrim: currentVehicleTrims,
							vehicleYear: applicableYear
						});
					}

					this.sendData('offer.click', offerClick);
				};

				proto.handleSpecialsEvent = function handleSpecialsEvent(
					eventData
				) {
					const {
						element,
						section,
						specialCategory,
						specialCta,
						specialCtaType,
						specialEvent,
						specialHeading,
						specialId,
						specialIncentiveType,
						specialMake,
						specialModel,
						specialPlacementType,
						specialYear
					} = eventData || {};
					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);
					const specialCategoryLowerCase =
						specialCategory.toLowerCase();
					// Slideshow clicks do not send element/section/text so we need fallbacks for those
					let position = specialPlacementType;
					if (section) {
						position = section;
					}
					const text =
						element && element.innerText
							? element.innerText
							: specialCta;

					if (
						(specialEvent === 'click' ||
							specialEvent === 'click-details') &&
						typeof specialCategory === 'string'
					) {
						let additionalData = {};
						let directCallEvent;

						if (
							specialCategoryLowerCase === 'service' ||
							specialCategoryLowerCase === 'parts'
						) {
							directCallEvent = `${specialCategoryLowerCase}.offer.click`;
							additionalData = {
								customLink: `${position}:${specialHeading}:${text}`
							};
						} else if (specialCategoryLowerCase === 'incentive') {
							const promoLabel = `${
								specialCtaType && specialCtaType
							} ${specialYear && ` - ${specialYear}`}${
								specialMake && ` ${specialMake}`
							}${specialModel && ` ${specialModel}`}${
								specialIncentiveType &&
								` - ${specialIncentiveType}`
							}`;
							directCallEvent = 'offer.click';
							additionalData = {
								customLink: `${position}:${promoLabel}:${text}`
							};
						}
						Object.assign(additionalData, {
							position,
							pageName,
							offerId: specialId,
							offerName: specialHeading,
							offerYear: specialYear,
							offerMake: specialMake,
							offerModel: specialModel
						});
						this.sendData(directCallEvent, additionalData);
					}
				};

				// Hproduct Click Events
				proto.handleHproductClick = function handleHproductClick(
					eventData
				) {
					const {
						bodyStyle,
						exteriorcolor,
						internetPrice,
						make,
						model,
						msrp,
						section,
						trim,
						type,
						vin,
						year,
						url,
						text
					} = eventData;

					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);

					this.sendData('link.hproduct.click', {
						customLink: `${section}:${vin || model}`,
						position: section,
						pageName,
						linkText: text,
						linkUrl: url,
						vehicleInfo: {
							modelYear: year,
							make,
							modelName: model,
							bodyStyle,
							trimLevel: trim,
							condition: type,
							vin,
							exteriorColor: exteriorcolor,
							internetPrice,
							msrp
						}
					});
				};

				proto.handleVehicleCompareCheck =
					function handleVehicleCompareCheck(eventData) {
						const {
							bodyStyle,
							exteriorcolor,
							internetPrice,
							make,
							model,
							msrp,
							section,
							trim,
							type,
							vin,
							year
						} = eventData;

						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);

						this.sendData('link.vehicleCompareCheck', {
							customLink: `${section}:${vin || model}`,
							position: section,
							pageName,
							vehicleInfo: {
								modelYear: year,
								make,
								modelName: model,
								bodyStyle,
								trimLevel: trim,
								condition: type,
								vin,
								exteriorColor: exteriorcolor,
								internetPrice,
								msrp
							}
						});
					};

				// MyCars Events
				proto.handleMycars = function handleMycars(
					eventData,
					action,
					name
				) {
					const {
						inventoryType,
						make,
						model,
						section,
						trim,
						vin,
						year
					} = eventData;

					const pageName = this.dataMapper.lookup(
						'digitalData.pageInfo.pageName'
					);

					this.sendData(`mycars.${action}`, {
						customLink: `${section}:${year} ${make} ${model} ${trim}:${name}`,
						position: section,
						pageName,
						vehicleInfo: {
							modelYear: year,
							make,
							modelName: model,
							trimLevel: trim,
							vin,
							condition: this.dataMapper.map(
								`inventoryType.${inventoryType}`
							)
						}
					});
				};

				proto.handleMycarsGetAlerts = function handleMycarsGetAlerts(
					eventData
				) {
					this.handleMycars(
						eventData,
						'get.alerts',
						'Get Price Alerts'
					);
				};

				proto.handleMycarsSave = function handleMycarsSave(eventData) {
					this.handleMycars(eventData, 'save', 'Save This Car');
				};

				proto.handleMycarsCompareCheck =
					function handleMycarsCompareCheck(eventData) {
						const status = eventData.element.checked
							? 'checked'
							: 'unchecked';
						this.handleMycars(
							eventData,
							`compare.${status}`,
							'Compare Checkbox'
						);
					};

				proto.handleMycarsCompareSubmit =
					function handleMycarsCompareSubmit(eventData) {
						const { section } = eventData;
						const pageName = this.dataMapper.lookup(
							'digitalData.pageInfo.pageName'
						);
						const vehicles = eventData.vehicles.map((vehicle) => {
							return {
								modelYear: vehicle.year,
								make: vehicle.make,
								modelName: vehicle.model,
								trimLevel: vehicle.trim,
								vin: vehicle.vin,
								condition: this.dataMapper.map(
									`inventoryType.${vehicle.condition}`
								)
							};
						});

						this.sendData('mycars.compare.submit', {
							customLink: `${section}:Compare Button Click`,
							position: section,
							pageName,
							vehicleInfo: vehicles
						});
					};

				// Maps Events
				proto.handleMapClick = function handleMapClick(_eventData) {
					this.sendData('map.show');
				};

				proto.handleVideoPlay = function handleVideoPlay(eventData) {
					// only need to know when the video first starts and not if the user jumps through the video
					if (
						(this.page.isVideoGalleryPage() ||
							this.page.isVehicleDetailPage(
								this.page.currentPageAlias()
							)) &&
						eventData.position === 0
					) {
						this.sendData('vdp.video.play', {
							fileType: eventData.fileType,
							videoDuration: eventData.videoDuration,
							videoSource: eventData.videoSource
						});
					}
				};

				proto.handleVideoMilestone = function handleVideoMilestone(
					eventData
				) {
					// different values for videoMilestones exist and we want to know when it is complete
					if (
						(this.page.isVideoGalleryPage() ||
							this.page.isVehicleDetailPage(
								this.page.currentPageAlias()
							)) &&
						eventData.videoMilestone === 'complete'
					) {
						this.sendData('vdp.video.complete', {
							fileType: eventData.fileType,
							videoDuration: eventData.videoDuration,
							videoSource: eventData.videoSource
						});
					}
				};
			})(AdobeLaunchEventHandler.prototype);

			return AdobeLaunchEventHandler;
		})();
};

export default runAdobeLaunchEventHandler;
